import type { AfterViewInit } from '@angular/core';
import {
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  signal,
} from '@angular/core';
import type { ReferenceElement } from '@floating-ui/dom';
import { autoUpdate, computePosition, flip, offset, Placement, shift } from '@floating-ui/dom';

/**
 * Positions the host element next to an anchor element using floating-ui.
 *
 * The host is moved to `document.body` and positioned with `strategy: 'fixed'` so it
 * escapes clipping from any `overflow`/`transform` ancestor.
 *
 * @example
 * ```html
 * <evc-dropdown-menu [evcAnchoredPosition]="triggerButton" placement="bottom-start" />
 * ```
 */
@Directive({
  selector: '[evcAnchoredPosition]',
  standalone: true,
})
export class AnchoredPositionDirective implements AfterViewInit {
  /** Element (or virtual element) the host is positioned relative to. */
  readonly evcAnchoredPosition = input.required<ReferenceElement>();
  /** Preferred placement; */
  readonly placement = input<Placement>('bottom-start');
  /** Gap in pixels between the anchor and the host. */
  readonly offset = input(8);
  /** Prevents the element from touching or overflowing the screen boundary. */
  readonly viewportPadding = input(8);

  readonly #el = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #renderer = inject(Renderer2);
  readonly #destroyRef = inject(DestroyRef);

  readonly #ready = signal(false);
  #positioned = false;

  // Re-run autoUpdate whenever the anchor changes so the host follows a new reference element.
  #repositionEffect = effect((onCleanup) => {
    if (!this.#ready()) return;

    const anchor = this.evcAnchoredPosition();
    const floating = this.#el.nativeElement;

    this.#positioned = false;
    this.#renderer.setStyle(floating, 'visibility', 'hidden');

    const cleanup = autoUpdate(anchor, floating, () => this.#updatePosition());
    onCleanup(() => cleanup());
  });

  ngAfterViewInit(): void {
    const floating = this.#el.nativeElement;

    this.#renderer.appendChild(document.body, floating);
    this.#renderer.setStyle(floating, 'position', 'fixed');

    this.#destroyRef.onDestroy(() => floating.remove());

    this.#ready.set(true);
  }

  #updatePosition(): void {
    const floating = this.#el.nativeElement;

    computePosition(this.evcAnchoredPosition(), floating, {
      strategy: 'fixed',
      placement: this.placement(),
      middleware: [
        offset(this.offset()),
        flip(),
        shift({ padding: this.viewportPadding() }),
      ],
    }).then(({ x, y }) => {
      this.#renderer.setStyle(floating, 'left', `${x}px`);
      this.#renderer.setStyle(floating, 'top', `${y}px`);
      if (!this.#positioned) {
        this.#positioned = true;
        this.#renderer.removeStyle(floating, 'visibility');
      }
    });
  }
}
